import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search, TrendingDown, TrendingUp, X } from 'lucide-react';
import { assetsService } from '../../services/assets.service';
import { Input } from '../ui';

const SEARCH_DEBOUNCE_MS = 300;

const fmtPrice = (n) =>
  n != null ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

// ── Asset picker ────────────────────────────────────────────────────────────
// Searchable combobox that resolves a real tradable asset (symbol + id) from
// the assets catalog, with a manual-symbol escape hatch. Mirrors CustomerPicker.
const AssetPicker = ({ value, onChange, placeholder = 'Search by symbol or name…', autoFocus }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [manualEntry, setManualEntry] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (selected) return undefined;
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setLoadError(false);
      setOpen(false);
      return undefined;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await assetsService.searchAssets({ search: trimmed, limit: 8 });
        const list = Array.isArray(data) ? data : Array.isArray(data?.assets) ? data.assets : [];
        setResults(list);
        setLoadError(false);
        setOpen(true);
      } catch {
        setResults([]);
        setLoadError(true);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, selected]);

  const handleSelect = (asset) => {
    setSelected(asset);
    setQuery('');
    setResults([]);
    setOpen(false);
    onChange(asset.symbol, asset);
  };

  const handleClearSelection = () => {
    setSelected(null);
    setQuery('');
    onChange('', null);
  };

  const handleManualChange = (e) => {
    onChange(e.target.value.toUpperCase(), null);
  };

  const exitManualEntry = () => {
    setManualEntry(false);
    onChange('', null);
  };

  if (selected) {
    const hasChange = selected.changePercent != null;
    const isUp = hasChange && selected.changePercent >= 0;
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-7 w-7 rounded-full bg-white border border-ink-200 flex items-center justify-center text-2xs font-semibold text-ink-600 shrink-0 overflow-hidden">
            {selected.logo ? (
              <img src={selected.logo} alt="" className="h-full w-full object-cover" />
            ) : (
              selected.symbol?.slice(0, 2)
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink-900 truncate">
              {selected.symbol} <span className="font-normal text-ink-500">· {selected.name}</span>
            </p>
            {selected.price != null && (
              <p className="text-xs text-ink-500 truncate flex items-center gap-1">
                {fmtPrice(selected.price)}
                {hasChange && (
                  <span className={`inline-flex items-center gap-0.5 ${isUp ? 'text-success-600' : 'text-danger-600'}`}>
                    {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {Math.abs(selected.changePercent).toFixed(2)}%
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleClearSelection}
          className="shrink-0 text-ink-400 hover:text-ink-700"
          aria-label="Change asset"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (manualEntry) {
    return (
      <div className="space-y-1.5">
        <Input mono autoFocus className="uppercase" value={value} onChange={handleManualChange} placeholder="e.g. AAPL" required />
        <button type="button" onClick={exitManualEntry} className="text-xs text-ink-500 hover:text-ink-700">
          Search assets instead
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
        <Input
          className="pl-9"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0 || loadError) setOpen(true); }}
          placeholder={placeholder}
          autoFocus={autoFocus}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {open && (
          <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-lg shadow-popover border border-ink-200 py-1 z-30 max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-ink-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching…
              </div>
            ) : loadError ? (
              <div className="px-3 py-3 text-sm text-danger-600">Couldn't load assets. Try again or enter the symbol manually below.</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-3 text-sm text-ink-500">No assets match "{query.trim()}".</div>
            ) : (
              results.map((a) => {
                const hasChange = a.changePercent != null;
                const isUp = hasChange && a.changePercent >= 0;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => handleSelect(a)}
                    className="w-full px-3 py-2 text-left hover:bg-ink-50 flex items-center gap-2.5"
                  >
                    <div className="h-7 w-7 rounded-full bg-ink-100 flex items-center justify-center text-2xs font-semibold text-ink-600 shrink-0 overflow-hidden">
                      {a.logo ? <img src={a.logo} alt="" className="h-full w-full object-cover" /> : a.symbol?.slice(0, 2)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink-900 truncate">
                        {a.symbol} <span className="font-normal text-ink-500">· {a.name}</span>
                      </p>
                    </div>
                    {a.price != null && (
                      <div className="text-right shrink-0">
                        <p className="text-xs font-mono text-ink-700 tnum">{fmtPrice(a.price)}</p>
                        {hasChange && (
                          <p className={`text-2xs font-medium ${isUp ? 'text-success-600' : 'text-danger-600'}`}>
                            {isUp ? '+' : ''}
                            {a.changePercent.toFixed(2)}%
                          </p>
                        )}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => setManualEntry(true)}
        className="mt-1.5 flex items-center gap-1 text-xs text-ink-400 hover:text-ink-600"
      >
        Enter symbol manually instead
      </button>
    </div>
  );
};

export default AssetPicker;
